import './timeregistration.css';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Panel } from '../../components/panel/panel';
import InputField from '../../components/input-field/input-field';
import TimeRegistrationTop from '../../components/timeregistration/top/timeregistration-top';
import TimeRegistrationDateRangeFilter from '../../components/timeregistration/date-range-filter/timeregistration-date-range-filter';
import { calculateAllUsersMetrics } from './timeregistration-utils';
import { useLogs } from '../../logsContext';
import TimeRegistrationUserRegistrations from '../../components/timeregistration/user-registrations/timeregistration-user-registrations';

const TimeRegistrationRegistrations = ({
  companyData,
  dateRange,
  setDateRange,
  employees,
  employeesToDisplay,
  setEmployeesToDisplay,
  valueToDisplay,
  setValueToDisplay,
  employeeToDisplayInFilter,
  setEmployeeToDisplayInFilter,
  weekMode,
  setWeekMode,
  periodType,
  setPeriodType,
  showInactive,
  setShowInactive,
  setModalEmployeeId,
  setModalDate,
  setIsModalOpen,
}) => {
  const { logs } = useLogs();
  const [metrics, setMetrics] = useState({});
  const locationState = useLocation().state;
  const processedEmployeeIdRef = useRef(null);

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  // Controlled period type tab for date range filter

  // Set employee filter if employeeId is passed in location state
  useEffect(() => {
    if (
      locationState?.employeeId &&
      locationState.employeeId !== processedEmployeeIdRef.current
    ) {
      const selectedEmployee = employees.find(
        (employee) => employee.id === locationState.employeeId
      );
      if (selectedEmployee) {
        setEmployeesToDisplay([selectedEmployee]);
        setEmployeeToDisplayInFilter([selectedEmployee]);
        processedEmployeeIdRef.current = locationState.employeeId;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.employeeId, employees]);

  // Handler for week mode toggle (now uses prop)
  const handleWeekModeChange = (e) => {
    setWeekMode(e.target.checked);
  };

  useEffect(() => {
    const logsByUser = logs.reduce((acc, log) => {
      const { userId } = log;
      if (!acc[userId]) {
        acc[userId] = [];
      }
      acc[userId].push(log);
      return acc;
    }, {});

    if (weekMode) {
      // --- Use calculateAllUsersMetrics for each week ---
      // Helper to get all week ranges in the date range
      const getWeekRanges = (startDate, endDate) => {
        const weeks = [];
        let current = new Date(startDate);
        current.setHours(0, 0, 0, 0);
        let weekStart = new Date(current);
        while (current <= new Date(endDate)) {
          // Find ISO week number and year
          const d = new Date(current);
          d.setHours(0, 0, 0, 0);
          d.setDate(d.getDate() + 4 - (d.getDay() || 7));
          const yearStart = new Date(d.getFullYear(), 0, 1);
          const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
          const year = d.getFullYear();
          // Find week end (Sunday)
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(
            weekStart.getDate() +
              6 -
              (weekStart.getDay() === 0 ? 6 : weekStart.getDay() - 1)
          );
          if (weekEnd > new Date(endDate))
            weekEnd.setTime(new Date(endDate).getTime());
          weeks.push({
            key: `${year}-U${weekNo}`,
            start: new Date(weekStart),
            end: new Date(weekEnd),
          });
          // Move to next week
          weekStart = new Date(weekEnd);
          weekStart.setDate(weekStart.getDate() + 1);
          current = new Date(weekStart);
        }
        return weeks;
      };

      const weekRanges = getWeekRanges(dateRange.startDate, dateRange.endDate);
      const weekMetrics = {};
      weekRanges.forEach(({ key, start, end }) => {
        const weekDateRange = {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
        };
        weekMetrics[key] = calculateAllUsersMetrics(
          logsByUser,
          employeesToDisplay,
          companyData,
          weekDateRange,
          false
        );
      });
      setMetrics(weekMetrics);
    } else {
      const allUsersMetrics = calculateAllUsersMetrics(
        logsByUser,
        employeesToDisplay,
        companyData,
        dateRange,
        true
      );
      setMetrics(allUsersMetrics);
    }
  }, [logs, employeesToDisplay, companyData, dateRange, weekMode]);

  return (
    <Panel
      noPadding
      inlineHeader
      header={
        <TimeRegistrationTop
          title="Registreringer"
          filters={
            <>
              <TimeRegistrationDateRangeFilter
                key={'filter-date'}
                name={'date'}
                value={dateRange}
                onChange={handleDateRangeChange}
                valueToDisplay={valueToDisplay}
                setValueToDisplay={setValueToDisplay}
                disableYear={true}
                periodType={periodType}
                setPeriodType={setPeriodType}
              />

              <InputField
                key={'filter-employee'}
                label={'Medarbejder'}
                className={'filter-employee'}
                type="select2"
                name={'employee'}
                isMulti
                options={employees
                  .filter((employee) => {
                    // Filter dropdown options based on showInactive state
                    if (showInactive) {
                      return (
                        employee?.data?.employmentStatus === 'inactive' &&
                        employee?.data?.endDate
                      );
                    } else {
                      return employee?.data?.employmentStatus !== 'inactive';
                    }
                  })
                  .map((employee) => ({
                    value: employee.id,
                    label: `${employee.data.firstName} ${employee.data.lastName}`,
                    target: {
                      value: employee.id,
                      name: 'employee',
                    },
                  }))}
                placeholder="- Vælg -"
                value={
                  Array.isArray(employeeToDisplayInFilter) &&
                  employeeToDisplayInFilter.length > 0
                    ? employeeToDisplayInFilter.map((emp) => ({
                        value: emp.id || emp.value,
                        label: emp.data
                          ? `${emp.data.firstName} ${emp.data.lastName}`
                          : emp.label,
                        target: {
                          value: emp.id || emp.value,
                          name: 'employee',
                        },
                      }))
                    : []
                }
                onChange={(selectedOptions) => {
                  if (
                    Array.isArray(selectedOptions) &&
                    selectedOptions.length > 0
                  ) {
                    const selectedEmployees = selectedOptions
                      .map((option) =>
                        employees.find(
                          (employee) => employee.id === option.value
                        )
                      )
                      .filter(Boolean);
                    setEmployeesToDisplay(selectedEmployees);
                    setEmployeeToDisplayInFilter(selectedEmployees);
                  } else {
                    // Clear the employee filter - show all employees (filtered by active/inactive status)
                    const filteredEmployees = employees.filter((employee) => {
                      if (showInactive) {
                        return (
                          employee?.data?.employmentStatus === 'inactive' &&
                          employee?.data?.endDate
                        );
                      } else {
                        return employee?.data?.employmentStatus !== 'inactive';
                      }
                    });
                    setEmployeesToDisplay(filteredEmployees);
                    setEmployeeToDisplayInFilter([]);
                  }
                }}
              />
              <InputField
                type="checkbox"
                label={'Vis inaktive'}
                name={'showInactive'}
                onChange={(e) => {
                  const checked = e.target ? e.target.checked : e;
                  setShowInactive(checked);
                  // Reset employee filter when switching between active/inactive
                  const filteredEmployees = employees.filter((employee) => {
                    if (checked) {
                      return (
                        employee?.data?.employmentStatus === 'inactive' &&
                        employee?.data?.endDate
                      );
                    } else {
                      return employee?.data?.employmentStatus !== 'inactive';
                    }
                  });
                  setEmployeesToDisplay(filteredEmployees);
                  setEmployeeToDisplayInFilter([]);
                }}
                checked={showInactive}
              />

              <InputField
                key={'filter-weekmode'}
                label={'Vis pr. uge'}
                type="checkbox"
                name={'weekMode'}
                checked={weekMode}
                onChange={handleWeekModeChange}
                style={{ marginLeft: 16 }}
              />
            </>
          }
        />
      }
    >
      <TimeRegistrationUserRegistrations
        employees={employeesToDisplay}
        metrics={metrics}
        setModalEmployeeId={setModalEmployeeId}
        setModalDate={setModalDate}
        setIsModalOpen={setIsModalOpen}
        weekMode={weekMode}
      />
    </Panel>
  );
};

export default TimeRegistrationRegistrations;
